import { c as _c } from "react/compiler-runtime";
import Logo from '@/assets/logos/cs/logo_gcss.svg';
import LogoSkipPay from '@/assets/logos/logo.svg';
import { LINKS, SELF_CARE_LINKS } from '@/constants/links';
import { useTranslation } from 'react-i18next';
import React, { useRef, Suspense, ReactNode, useState, useMemo } from 'react';
import { useClientOnly } from '@/hooks/useClientOnly';
import { mergeClasses } from '@/utils/style';
import { Button } from '@/components/buttons';
import { Link } from '@/components/links';
import { Section } from './useGcssSections';
import { GcssSubsection } from './GcssSubsection';
import UserCircleIcon from '@/assets/icons/heroIcons/User-circle.svg';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { hasWindow } from '@/utils/dom';
import { useInAppDetect } from '@/hooks/useInAppDetect';
import { ClientGate } from '@/components/ClientGate';
import { useHeader } from '@/sections/layout/Header/useHeader';
const variants = {
  open: () => ({
    x: 0,
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  }),
  closed: ({
    isResponsive
  }: VariantProps) => ({
    x: isResponsive ? 0 : '100%',
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  })
};
type VariantProps = {
  windowWidth: number;
  isResponsive: boolean;
};
const navButtonsDimensions = 'h-10 2xl:h-12 4xl:h-14 font-medium!';
function ClientNavFallback(t0) {
  const $ = _c(4);
  const {
    headerSections
  } = t0;
  let t1;
  if ($[0] !== headerSections) {
    t1 = headerSections.map(_temp2);
    $[0] = headerSections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <nav className="h-10 w-10 opacity-0 lg:h-18 lg:w-18">{t1}</nav>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(section) {
  return <ul key={section.id}><li>{section.subsection && <><h4>{section.name}</h4><ul>{section.subsection.map(_temp)}</ul></>}</li></ul>;
}
function _temp(subsection) {
  return <li key={subsection.id} />;
}
export function GcssHeader(t0) {
  const $ = _c(97);
  const {
    headerSections,
    variant,
    search
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["core", "gcss"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const refList = useRef(null);
  const {
    isClient
  } = useClientOnly();
  const [listScrollProgress, setListScrollProgress] = useState(0);
  const [listScroll, setListScroll] = useState(0);
  const t2 = isClient ? refList : undefined;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      container: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    scrollXProgress,
    scrollX
  } = useScroll(t3);
  useMotionValueEvent(scrollXProgress, "change", setListScrollProgress);
  useMotionValueEvent(scrollX, "change", setListScroll);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      headerRef: ref
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    debouncedOpenedSubmenu,
    isOpenMobileMenu,
    isResponsive,
    isLoggedIn,
    scrolling,
    handleSubsectionOpen,
    handleSubsectionClose,
    setOpenedSubmenu,
    toggleLockScroll
  } = useHeader(t4);
  const isInApp = useInAppDetect();
  let t5;
  if ($[4] !== headerSections) {
    t5 = <ClientNavFallback headerSections={headerSections} />;
    $[4] = headerSections;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const fallback = t5;
  if (isInApp) {
    return null;
  }
  const t6 = scrolling ? "shadow-floating bg-white" : "bg-white xl:bg-transparent";
  let t7;
  if ($[6] !== t6) {
    t7 = mergeClasses("fixed z-50 mx-auto flex max-w-[1792px] flex-col gap-3", "transition-all duration-300", "4xl:rounded-2xl 4xl:pl-4 right-0 left-0 px-4 py-2.5 sm:py-3 xl:top-4 xl:right-4 xl:left-4 xl:rounded-xl xl:py-0", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = LINKS();
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = t8.gcssHomepage;
  const t10 = variant === "inverted" && !scrolling ? "text-white!" : "text-gray-900!";
  let t11;
  if ($[9] !== t10) {
    t11 = mergeClasses("h-8 transition-colors duration-200 xl:h-20", t10);
    $[9] = t10;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t) {
    t12 = t("core:link.homepage");
    $[11] = t;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Logo className="hidden h-full w-auto xl:block" />;
    t14 = <LogoSkipPay className="h-full w-auto xl:hidden" />;
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] !== t11 || $[16] !== t12) {
    t15 = <Link to={t9} analyticsParam="headerHome" testId="headerHome" className={t11} aria-label={t12}>{t13}{t14}</Link>;
    $[15] = t11;
    $[16] = t12;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const t16 = isResponsive ? "block" : isOpenMobileMenu ? "block" : "hidden";
  let t17;
  if ($[18] !== t16) {
    t17 = mergeClasses("xl-gap-4 3xl:gap-5 4xl:gap-8 hidden gap-4 overflow-hidden xl:flex xl:transform-none! xl:overflow-visible", t16);
    $[18] = t16;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== isResponsive) {
    t18 = {
      windowWidth: hasWindow() ? window.innerWidth : 0,
      isResponsive
    };
    $[20] = isResponsive;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = isResponsive ? "open" : isOpenMobileMenu ? "open" : "closed";
  let t20;
  if ($[22] !== debouncedOpenedSubmenu || $[23] !== handleSubsectionClose || $[24] !== handleSubsectionOpen || $[25] !== headerSections || $[26] !== scrolling || $[27] !== setOpenedSubmenu || $[28] !== variant) {
    let t21;
    if ($[30] !== debouncedOpenedSubmenu || $[31] !== handleSubsectionClose || $[32] !== handleSubsectionOpen || $[33] !== scrolling || $[34] !== setOpenedSubmenu || $[35] !== variant) {
      t21 = section => <li key={section.id} onMouseEnter={() => handleSubsectionOpen(section.id)} onMouseLeave={handleSubsectionClose} onFocus={() => handleSubsectionOpen(section.id, {
        immediate: true
      })} onBlur={handleSubsectionClose}><Suspense>{section.subsection && <GcssSubsection isOpen={debouncedOpenedSubmenu === section.id} handleClick={() => {
            setOpenedSubmenu(value => {
              if (value === section.id) {
                return null;
              }
              return section.id;
            });
          }} sectionName={section.name} sectionId={section.id} subsection={section.subsection} isScrolling={scrolling} isSectionActive={false} variant={variant} />}</Suspense></li>;
      $[30] = debouncedOpenedSubmenu;
      $[31] = handleSubsectionClose;
      $[32] = handleSubsectionOpen;
      $[33] = scrolling;
      $[34] = setOpenedSubmenu;
      $[35] = variant;
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    t20 = headerSections.map(t21);
    $[22] = debouncedOpenedSubmenu;
    $[23] = handleSubsectionClose;
    $[24] = handleSubsectionOpen;
    $[25] = headerSections;
    $[26] = scrolling;
    $[27] = setOpenedSubmenu;
    $[28] = variant;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[37] !== search) {
    t21 = search && <li><div className="3xl:w-72 flex h-full w-50 flex-col items-center justify-center 2xl:w-60">{search}</div></li>;
    $[37] = search;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t20 || $[40] !== t21) {
    t22 = <ul className="4xl:gap-8 hidden h-[calc(100%-14rem)] flex-col overflow-x-auto xl:flex xl:flex-row xl:items-center xl:gap-4 xl:overflow-visible xl:pb-0 2xl:gap-6">{t20}{t21}</ul>;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t22 || $[46] !== toggleLockScroll) {
    t23 = <motion.nav className={t17} variants={variants} custom={t18} animate={t19} initial="closed" onAnimationComplete={toggleLockScroll}>{t22}</motion.nav>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t22;
    $[46] = toggleLockScroll;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== fallback || $[49] !== t23) {
    t24 = <ClientGate fallback={fallback}>{t23}</ClientGate>;
    $[48] = fallback;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== isLoggedIn || $[52] !== t) {
    t25 = <div className="ml-auto hidden items-center space-x-3 xl:flex">{isLoggedIn ? <Button variant="primary" analyticsParam="selfCareOverview" testId="selfCareOverview" to={SELF_CARE_LINKS.overview} className={mergeClasses("w-[15.5rem]!", navButtonsDimensions)} leftIcon={<UserCircleIcon className="-my-2 h-7 w-7" />}>{t("header.loggedIn.button")}</Button> : <><Button variant="primary" analyticsParam="headerRegister" testId="headerRegister" to={LINKS().registration} className={mergeClasses("3xl:w-50 xl:w-32 2xl:w-36", navButtonsDimensions)}>{t("gcss:header.register.button")}</Button></>}</div>;
    $[51] = isLoggedIn;
    $[52] = t;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = mergeClasses("ml-auto flex w-32 justify-center px-4 py-2 xl:hidden", navButtonsDimensions);
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = LINKS();
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const t28 = t27.registration;
  let t29;
  if ($[56] !== t) {
    t29 = t("gcss:header.register.button");
    $[56] = t;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t29) {
    t30 = <Button className={t26} variant="primary" analyticsParam="headerMobileRegister" testId="headerMobileRegister" to={t28}>{t29}</Button>;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t15 || $[61] !== t24 || $[62] !== t25 || $[63] !== t30) {
    t31 = <div className="4xl:gap-16 flex items-center gap-6 2xl:gap-4">{t15}{t24}{t25}{t30}</div>;
    $[60] = t15;
    $[61] = t24;
    $[62] = t25;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== search) {
    t32 = search && <div className="w-full xl:hidden">{search}</div>;
    $[65] = search;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  const t33 = listScroll === 0 ? 0 : listScrollProgress / 0.1;
  let t34;
  if ($[67] !== t33) {
    t34 = <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-16 bg-linear-to-r from-white to-transparent" style={{
      opacity: t33
    }} />;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  const t35 = (1 - listScrollProgress) / 0.1;
  let t36;
  if ($[69] !== t35) {
    t36 = <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-16 bg-linear-to-l from-white to-transparent" style={{
      opacity: t35
    }} />;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== debouncedOpenedSubmenu || $[72] !== handleSubsectionClose || $[73] !== handleSubsectionOpen || $[74] !== headerSections || $[75] !== scrolling || $[76] !== setOpenedSubmenu || $[77] !== variant) {
    let t38;
    if ($[79] !== debouncedOpenedSubmenu || $[80] !== handleSubsectionClose || $[81] !== handleSubsectionOpen || $[82] !== scrolling || $[83] !== setOpenedSubmenu || $[84] !== variant) {
      t38 = section_0 => <li key={section_0.id} onMouseEnter={() => handleSubsectionOpen(section_0.id)} onMouseLeave={handleSubsectionClose} onFocus={() => handleSubsectionOpen(section_0.id, {
        immediate: true
      })} onBlur={handleSubsectionClose}><Suspense>{section_0.subsection && <GcssSubsection isOpen={debouncedOpenedSubmenu === section_0.id} handleClick={() => {
            setOpenedSubmenu(value_0 => {
              if (value_0 === section_0.id) {
                return null;
              }
              return section_0.id;
            });
          }} sectionName={section_0.name} sectionId={section_0.id} key={section_0.id} subsection={section_0.subsection} isScrolling={scrolling} isSectionActive={false} variant={variant} />}</Suspense></li>;
      $[79] = debouncedOpenedSubmenu;
      $[80] = handleSubsectionClose;
      $[81] = handleSubsectionOpen;
      $[82] = scrolling;
      $[83] = setOpenedSubmenu;
      $[84] = variant;
      $[85] = t38;
    } else {
      t38 = $[85];
    }
    t37 = headerSections.map(t38);
    $[71] = debouncedOpenedSubmenu;
    $[72] = handleSubsectionClose;
    $[73] = handleSubsectionOpen;
    $[74] = headerSections;
    $[75] = scrolling;
    $[76] = setOpenedSubmenu;
    $[77] = variant;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[86] !== t37) {
    t38 = <ul className="no-scrollbar relative flex flex-row gap-5 overflow-x-auto px-4 xl:px-0" ref={refList}>{t37}</ul>;
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== t34 || $[89] !== t36 || $[90] !== t38) {
    t39 = <div className="-mx-4 xl:hidden"><div className="relative">{t34}{t36}{t38}</div></div>;
    $[88] = t34;
    $[89] = t36;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t31 || $[93] !== t32 || $[94] !== t39 || $[95] !== t7) {
    t40 = <header ref={ref} className={t7}>{t31}{t32}{t39}</header>;
    $[92] = t31;
    $[93] = t32;
    $[94] = t39;
    $[95] = t7;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  return t40;
}